import MarkdownIt from 'markdown-it';
import { splitLinks } from './linkify';

/**
 * Formatted text in descriptions and notes: bold, italic, bullet and numbered
 * lists, and links, saved as Markdown (`**bold**`, `*italic*`, `- item`,
 * `1. item`, `[text](https://…)`).
 *
 * Text written before formatting existed is plain text, and it reads the same:
 * each line break is kept (markdown-it `breaks`), and nothing else in it
 * changes meaning except a line starting with "- " or "1. ", which becomes the
 * list it was meant to be.
 *
 * Only those few rules are switched on. Headings, quotes, code, tables, images
 * and raw HTML are off, so "# 3 floors" or "<b>" stay as typed, and nothing
 * typed is ever turned into HTML: the result here is a small tree of blocks
 * and text runs that components/RichText.js draws as React elements.
 *
 * Kept in step with mobile/src/utils/richText.ts and server/utils/richText.js.
 */
const md = new MarkdownIt('zero', { breaks: true })
    .enable(['list', 'emphasis', 'link', 'autolink', 'newline', 'escape', 'entity']);

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;
const safeHref = (href) => {
    const h = String(href || '').trim();
    if (SAFE_HREF.test(h)) return h;
    if (/^www\./i.test(h)) return `https://${h}`;
    return null;
};

// One inline token's children → text runs: { text, bold, italic, href } or { br: true }.
const inlineRuns = (children = [], linkify = true) => {
    const runs = [];
    let bold = 0;
    let italic = 0;
    const links = [];
    const push = (text) => {
        if (!text) return;
        const href = links.length ? links[links.length - 1] : null;
        const mark = { bold: bold > 0, italic: italic > 0 };
        if (href) {
            runs.push({ text, ...mark, href });
            return;
        }
        if (!linkify) {
            runs.push({ text, ...mark, href: null });
            return;
        }
        // Bare links in the text are links too, by the same rule as chat.
        splitLinks(text).forEach((part) => runs.push({ text: part.text, ...mark, href: part.href || null }));
    };
    children.forEach((t) => {
        switch (t.type) {
            case 'text': push(t.content); break;
            case 'strong_open': bold += 1; break;
            case 'strong_close': bold = Math.max(0, bold - 1); break;
            case 'em_open': italic += 1; break;
            case 'em_close': italic = Math.max(0, italic - 1); break;
            case 'link_open': links.push(safeHref(t.attrGet('href'))); break;
            case 'link_close': links.pop(); break;
            case 'softbreak':
            case 'hardbreak': runs.push({ br: true }); break;
            default: if (t.content) push(t.content);
        }
    });
    return runs;
};

/**
 * Text → blocks:
 *   { type: 'p', runs }
 *   { type: 'ul' | 'ol', start, items: [[block, …], …] }
 * `linkify: false` leaves bare addresses as text (the editor does).
 */
export const parseRichText = (text, { linkify = true } = {}) => {
    const source = String(text ?? '').replace(/\r\n?/g, '\n');
    if (!source.trim()) return [];
    const tokens = md.parse(source, {});
    let i = 0;

    const readBlocks = (closeType) => {
        const blocks = [];
        while (i < tokens.length) {
            const t = tokens[i];
            if (closeType && t.type === closeType) { i += 1; return blocks; }
            if (t.type === 'paragraph_open') {
                const inline = tokens[i + 1];
                blocks.push({ type: 'p', runs: inlineRuns(inline?.children, linkify) });
                i += 3; // open, inline, close
            } else if (t.type === 'bullet_list_open' || t.type === 'ordered_list_open') {
                const ordered = t.type === 'ordered_list_open';
                const start = ordered ? Number(t.attrGet('start')) || 1 : 1;
                const close = ordered ? 'ordered_list_close' : 'bullet_list_close';
                i += 1;
                const items = [];
                while (i < tokens.length && tokens[i].type !== close) {
                    if (tokens[i].type === 'list_item_open') {
                        i += 1;
                        items.push(readBlocks('list_item_close'));
                    } else {
                        i += 1;
                    }
                }
                i += 1;
                blocks.push({ type: ordered ? 'ol' : 'ul', start, items });
            } else if (t.type === 'inline') {
                blocks.push({ type: 'p', runs: inlineRuns(t.children, linkify) });
                i += 1;
            } else {
                i += 1;
            }
        }
        return blocks;
    };

    return readBlocks(null);
};

const runsText = (runs, { link }) => runs.map((r) => {
    if (r.br) return '\n';
    if (r.href && link && r.text !== r.href && `https://${r.text}` !== r.href) return `${r.text} (${r.href})`;
    return r.text;
}).join('');

// Between blocks: an empty line, except a list right under a line stays right under it.
const gap = (blocks, i, depth) => (depth || (blocks[i].type !== 'p' && blocks[i - 1].type === 'p') ? '\n' : '\n\n');

const blocksText = (blocks, opts, depth = 0) => {
    const pad = '  '.repeat(depth);
    return blocks.map((b) => {
        if (b.type === 'p') return runsText(b.runs, opts).split('\n').map((l) => pad + l).join('\n');
        return b.items.map((item, n) => {
            const marker = b.type === 'ol' ? `${b.start + n}.` : opts.bullet;
            const [first, ...rest] = item;
            const head = first && first.type === 'p' ? runsText(first.runs, opts).replace(/\n/g, ' ') : '';
            const tail = (first && first.type !== 'p' ? [first, ...rest] : rest);
            const lines = [`${pad}${marker} ${head}`.trimEnd()];
            if (tail.length) lines.push(blocksText(tail, opts, depth + 1));
            return lines.join('\n');
        }).join('\n');
    }).reduce((out, part, i) => (i ? out + gap(blocks, i, depth) + part : part), '');
};

/**
 * The words without the marks, for one-line previews, notifications and
 * anything that leaves the portal as plain text. Lists keep a • or number,
 * a link written as [text](url) keeps its address in brackets.
 */
export const toPlainText = (text, { link = true, bullet = '•' } = {}) => {
    const blocks = parseRichText(text);
    if (!blocks.length) return String(text ?? '').trim();
    return blocksText(blocks, { link, bullet }).trim();
};

/** The plain text on one line, for card previews. */
export const toPreviewText = (text) => toPlainText(text, { link: false }).replace(/\s*\n+\s*/g, ' ').trim();

export default parseRichText;
